import { useCallback, useRef, useState } from 'react';
import { Keyboard } from 'react-native';
import { useTranslation } from 'react-i18next';

import { InputType, useConfirm, useInput } from '@pletly/ui';
import { useUser } from '%hooks';
import {
	useCommentPostMutation,
	useRemoveCommentMutation,
	useUpdateCommentMutation,
} from './__generated__/post.mutation.generated';
import { GetPostDocument, GetPostQuery } from './__generated__/post.generated';

export const useCommentActions = (postId: string) => {
	const { t } = useTranslation();
	const user = useUser();
	const { confirm } = useConfirm();
	const input = useInput();
	const inputRef = useRef<InputType>(null);

	const [selectedComment, setSelectedComment] = useState<GetPost_post_comments>();
	const [isEditMode, setIsEditMode] = useState(false);
	const blockedCommenting = user.role === 'USER' && user.restrictedAccess;

	const [commentPost] = useCommentPostMutation({
		variables: { postId: postId, comment: input.value },
		refetchQueries: [GetPostDocument],
	});
	const [removeComment] = useRemoveCommentMutation({
		refetchQueries: [GetPostDocument],
	});
	const [updateComment] = useUpdateCommentMutation({
		refetchQueries: [GetPostDocument],
	});

	const showCommentActions = useCallback(
		(comment: GetPost_post_comments) => () => {
			if (comment.author.id !== user.id.toString()) {
				return;
			}

			setSelectedComment(comment);
		},
		[user.id],
	);

	const handleCreateComment = useCallback(async () => {
		try {
			await commentPost();
			input.reset();
		} catch (error) {
			console.error(error);
		}
	}, [commentPost, input]);

	const handleUpdateComment = useCallback(async () => {
		if (!selectedComment) {
			return;
		}

		try {
			await updateComment({
				variables: {
					data: {
						id: Number(selectedComment.id),
						content: input.value,
					},
				},
			});
			input.reset();
			setSelectedComment(undefined);
			setIsEditMode(false);
		} catch (error) {
			console.error(error);
		}
	}, [selectedComment, updateComment, input]);

	const handleDeleteComment = useCallback(async () => {
		if (!selectedComment) {
			return;
		}
		try {
			await removeComment({
				variables: {
					data: {
						id: Number(selectedComment.id),
					},
				},
			});
			setSelectedComment(undefined);
		} catch (error) {
			console.error(error);
		}
	}, [selectedComment, removeComment]);

	const handleEditCommentClick = useCallback(() => {
		if (!selectedComment) {
			return;
		}
		setIsEditMode(true);

		input.onChangeText(selectedComment.content);
		setTimeout(() => inputRef.current?.focus(), 100);
	}, [selectedComment, input]);

	const handleDeleteCommentClick = useCallback(async () => {
		await confirm(t('post.delete-comment-title'), t('post.delete-comment-content'));
		if (!confirm) {
			setSelectedComment(undefined);

			return;
		}

		return handleDeleteComment();
	}, [confirm, t, handleDeleteComment]);

	const resetCommentEditState = useCallback(() => {
		setSelectedComment(undefined);
		setIsEditMode(false);
	}, []);

	const cancelCommentEdit = useCallback(() => {
		resetCommentEditState();
		input.reset();
		inputRef.current?.blur();
	}, [input, resetCommentEditState]);

	const submitComment = useCallback(() => {
		Keyboard.dismiss();
		if (isEditMode) {
			return handleUpdateComment();
		}

		return handleCreateComment();
	}, [isEditMode, handleUpdateComment, handleCreateComment]);

	return {
		showCommentActions,
		handleEditCommentClick,
		handleDeleteCommentClick,
		selectedComment,
		setSelectedComment,
		commentInput: input,
		commentInputRef: inputRef,
		isEditMode,
		cancelCommentEdit,
		submitComment,
		blockedCommenting,
	};
};

type GetPost_post_comments = GetPostQuery['post']['comments'][number];
